import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BackButton } from '../components/BackButton'
import { ConversationImageList } from '../components/ConversationImageList'
import { ConversationList } from '../components/ConversationList'
import { CustomExplain } from '../components/CustomExplain'
import { CustomTitle } from '../components/CustomTitile'
import { EditButton } from '../components/EditButton'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { SaveButton } from '../components/SaveButton'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '../hooks/useAuth'
import { useC802ConversationGet } from '../hooks/useC802ConversationGet'
import { useC804ConversationRecordSave } from '../hooks/useC804ConversationRecordSave'
import { useConversation } from '../hooks/useConversation'
import type { ConversationIllustrationInfo, ConversationInfo } from '../interfaces/ConversationIf'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'

// 会話内容画面
export const Conversation = () => {
  const auth = useAuth()
  const conv = useConversation()
  const c802 = useC802ConversationGet()
  const c804 = useC804ConversationRecordSave()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // ローディングステータス監視
  useEffect(() => {
    if (c802.loading === loadStatus.LOADING || c804.loading === loadStatus.LOADING) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c802.loading, c804.loading])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // 会話配列
  const [convs, setConvs] = useState<ConversationInfo[]>([])
  // image配列
  const [images, setImages] = useState<ConversationIllustrationInfo[]>([])
  const [tmpImages, setTmpImages] = useState<ConversationIllustrationInfo[]>([])
  // 編集モード
  const [editMode, setEditMode] = useState(false)

  // 保存ボタン
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  // 画像削除状態
  const [isImageDeleted, setIsImageDeleted] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      const result = await c802.execConversationGet(conv.convId)
      if (result.success) {
        if (result.info) {
          result.info!.conversationList.forEach(conversation => {
            // 会話記録を編集対象に設定
            if (conv.orgText) {
              conversation.text_before_editing = conversation.org_text
              conversation.text_being_edited = conversation.org_text
              conversation.text_editing_temporary_save = conversation.org_text
              setImages(result.info!.org_illustrationList)
              setTmpImages(result.info!.org_illustrationList)
            } else {
              conversation.text_before_editing = conversation.edit_text
              conversation.text_being_edited = conversation.edit_text
              conversation.text_editing_temporary_save = conversation.edit_text
              setImages(result.info!.edit_illustrationList)
              setTmpImages(result.info!.edit_illustrationList)
            }
          })
          setConvs(result.info!.conversationList)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigate = useNavigate()

  // 会話編集ハンドラ
  const convEditHandler = () => {
    setEditMode(true)
  }
  // 会話編集キャンセルハンドラ
  const convCancelHandler = () => {
    // 会話を元に戻す。
    convs.forEach(conversation => {
      if (conv.orgText) {
        conversation.text_before_editing = conversation.org_text
        conversation.text_being_edited = conversation.org_text
        conversation.text_editing_temporary_save = conversation.org_text
      } else {
        conversation.text_before_editing = conversation.edit_text
        conversation.text_being_edited = conversation.edit_text
        conversation.text_editing_temporary_save = conversation.edit_text
      }
    })
    setImages(tmpImages)
    setEditMode(false)
    setIsButtonDisabled(true)
  }
  // 会話編集保存ハンドラ
  const convSaveHandler = async () => {
    setIsButtonDisabled(true)
    setEditMode(false)
    const result = await c804.execConversationRecordSave(conv.convId, convs, images)
    if (result.success) {
      navigate('/conversationLog')
    } else {
      // 会話を元に戻す。
      convs.forEach(conversation => {
        if (conv.orgText) {
          conversation.text_before_editing = conversation.org_text
          conversation.text_being_edited = conversation.org_text
          conversation.text_editing_temporary_save = conversation.org_text
        } else {
          conversation.text_before_editing = conversation.edit_text
          conversation.text_being_edited = conversation.edit_text
          conversation.text_editing_temporary_save = conversation.edit_text
        }
      })
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 会話イメージ削除ハンドラ
  const conDelImageHandler = (id: string) => {
    if (editMode) {
      setImages(prevImages => prevImages.filter(img => img.illustration_id !== id))
      setIsButtonDisabled(false)
      // 画像削除
      setIsImageDeleted(true)
    }
  }

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }
  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let conversationContentJsx: JSX.Element
  switch (baseLanguage) {
    case 'es':
      conversationContentJsx = (
        <Typography
          sx={{
            font: 'normal normal bold 32px/48px Noto Sans JP',
            fontSize: '28px',
          }}
        >
          {translation['label_conversation_content']}
        </Typography>
      )
      break
    default:
      conversationContentJsx = (
        <Typography
          sx={{
            font: 'normal normal bold 32px/48px Noto Sans JP',
          }}
        >
          {translation['label_conversation_content']}
        </Typography>
      )
      break
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1 }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <Box>
              <CustomTitle>{translation['label_conversation_history']}</CustomTitle>
            </Box>
            <Box>
              <CustomExplain>{translation['message_conversation_history_info']}</CustomExplain>
            </Box>
            <Box
              sx={{
                marginTop: '40px',
              }}
            >
              <Paper
                sx={{
                  margin: '0px 64px 0px 0px',
                  maxWidth: '100%',
                  width: '1418px',
                  overflowX: 'auto',
                  height: 'calc(100vh - 330px)',
                }}
                elevation={2}
              >
                <Box
                  sx={{
                    p: '10px',
                    height: 50,
                    bgcolor: editMode ? '#fff16f' : 'white',
                  }}
                >
                  <Grid container alignItems="center" justifyContent="space-around" spacing={2}>
                    <Grid item xs={3}>
                      {!editMode && (
                        <BackButton
                          title={translation['button_back_to_list']}
                          onClick={() => navigate('/conversationLog')}
                        />
                      )}
                    </Grid>
                    <Grid item xs={4} textAlign={'center'}>
                      {conversationContentJsx}
                    </Grid>
                    <Grid item xs={3}>
                      <Box display="flex" justifyContent="flex-end" sx={{ gap: '16px' }}>
                        {editMode ? (
                          <>
                            <BackButton
                              title={translation['button_cancel']}
                              onClick={convCancelHandler}
                            />
                            <SaveButton
                              title={translation['button_save']}
                              onClick={convSaveHandler}
                              isButtonDisabled={isButtonDisabled}
                            />
                          </>
                        ) : (
                          <EditButton
                            title={translation['button_edit']}
                            onClick={convEditHandler}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Grid
                  container
                  spacing={2}
                  justifyContent="space-around"
                  style={{
                    height: 'calc(100vh - 400px)',
                    overflow: 'auto',
                    marginTop: '0px',
                  }}
                >
                  {images.length === 0 ? (
                    <Grid
                      item
                      xs={12}
                      style={{
                        paddingTop: '0px',
                      }}
                    >
                      <ConversationList
                        editMode={editMode}
                        items={convs}
                        setItems={setConvs}
                        setIsButtonDisabled={setIsButtonDisabled}
                        isImageDeleted={isImageDeleted}
                      />
                    </Grid>
                  ) : (
                    <>
                      <Grid
                        item
                        xs={3}
                        style={{
                          paddingTop: '0px',
                        }}
                      >
                        <ConversationImageList
                          editMode={editMode}
                          items={images}
                          onDeleteImage={conDelImageHandler}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={9}
                        style={{
                          paddingTop: '0px',
                          paddingLeft: '0px',
                        }}
                      >
                        <ConversationList
                          editMode={editMode}
                          items={convs}
                          setItems={setConvs}
                          setIsButtonDisabled={setIsButtonDisabled}
                          isImageDeleted={isImageDeleted}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Box>
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              {(c802.loading === loadStatus.LOADING || c804.loading === loadStatus.LOADING) && (
                <CircularProgress />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
